import { LOCAL_SIGNER_DEFAULT_PORT } from '@autosign/constants'
import { autosignApi } from '@autosign/providers/autosign/api'
import { IconDeviceUsb, IconExternalLink } from '@tabler/icons-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { RetrieveSignatureSessionResponseBody } from 'bff'
import createFetchClient from 'openapi-fetch'
import qs from 'qs'
import React, { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button, ButtonProps } from 'ui/components/button'
import { useSigning } from '../../../../../components/Signing/Context'
import { useReactiveSignatureSession } from '../../../../../hooks/useReactiveSignatureSession'
import { buildSignatureSessionQueryKey } from '../../../../../hooks/useSignatureSessionQuery'
import { useGeolocationCtx } from '../../../../../providers/GeolocationContext'
import { usePDFStore } from '../../../../../stores/pdf.store'
import {
  SignatureSessionMethodProps,
  withSignatureSessionOnMount,
} from './SignatureSession'
import { WindowsLogo } from './WindowsLogo'
import { useSignatureSession } from '../../../../../hooks/useSignatureSession'

const localSignerHttpClient = createFetchClient<{
  '/certificate': {
    parameters: {
      query?: never
      header?: never
      path?: never
      cookie?: never
    }
    get: {
      parameters: {
        query?: never
        header?: never
        path?: never
        cookie?: never
      }
      requestBody?: never
      responses: {
        200: {
          headers: {
            [name: string]: unknown
          }
          content: {
            'application/json': {
              certificate: string
              certificateChain: string[]
            }
          }
        }
      }
    }
    put?: never
    post?: never
    delete?: never
    options?: never
    head?: never
    patch?: never
    trace?: never
  }
  '/sign': {
    parameters: {
      query?: never
      header?: never
      path?: never
      cookie?: never
    }
    post: {
      parameters: {
        query?: never
        header?: never
        path?: never
        cookie?: never
      }
      requestBody: {
        content: {
          'application/json': {
            algorithm: string
            oid: string
            uri: string
            base64Value: string
            hexValue: string
            toBeSigned: string
          }
        }
      }
      responses: {
        200: {
          headers: {
            [name: string]: unknown
          }
          content: {
            'application/json': {
              signature: string
              signatureHex: string
              algorithm: string
              certificate: string
              certificateChain: string[]
            }
          }
        }
      }
    }
    put?: never
    get?: never
    delete?: never
    options?: never
    head?: never
    patch?: never
    trace?: never
  }
  '/pair': {
    parameters: {
      query?: never
      header?: never
      path?: never
      cookie?: never
    }
    post: {
      parameters: {
        query?: never
        header?: never
        path?: never
        cookie?: never
      }
      requestBody: {
        content: {
          'application/json': {
            transactionId: string
            fieldId: string
            signatureSessionId: string
            token: string
          }
        }
      }
      responses: {
        200: {
          headers: {
            [name: string]: unknown
          }
          content: {
            'application/json': {
              signature: string
              signatureHex: string
              algorithm: string
              certificate: string
              certificateChain: string[]
            }
          }
        }
      }
    }
    put?: never
    get?: never
    delete?: never
    options?: never
    head?: never
    patch?: never
    trace?: never
  }
}>({
  mode: 'cors',
  baseUrl: `http://localhost:${LOCAL_SIGNER_DEFAULT_PORT}`,
})

const CTAButton = React.forwardRef<
  React.ElementRef<typeof Button>,
  ButtonProps
>((props, ref) => {
  return (
    <Button className='w-full' ref={ref} {...props}>
      Ya conecté el token, firmar
    </Button>
  )
})

const useTokenSignature = (
  signatureSession: RetrieveSignatureSessionResponseBody
) => {
  const [isSigning, setIsSigning] = useState(false)
  const [signed, setSigned] = useState(false)

  const signing = useSigning()
  const currentFocusFieldId = usePDFStore((state) => state.currentFocusFieldId)

  const geolocation = useGeolocationCtx()

  const queryClient = useQueryClient()

  const handleSign = useCallback(async () => {
    setIsSigning(true)

    const certificate = await localSignerHttpClient.GET('/certificate')

    if (certificate.error) {
      setIsSigning(false)

      console.error(
        `Something went wrong when getting the certificate from the token usb`,
        certificate
      )

      return toast.error('Error al obtener certificado', {
        description: `No se logró obtener el certificado del token usb.`,
      })
    }

    const dataToBeSigned = await autosignApi.POST(
      '/v1/signing/{transactionId}/signature/{fieldId}/session/{sessionId}/prepare/dtbs',
      {
        params: {
          header: {
            authorization: signing.token,
          },
          path: {
            fieldId: currentFocusFieldId!,
            transactionId: signing.id,
            sessionId: signatureSession.id,
          },
        },
        body: certificate.data,
      }
    )

    if (dataToBeSigned.error) {
      setIsSigning(false)

      console.error(
        `Something went wrong when preparing the data to be signed`,
        dataToBeSigned
      )

      return toast.error('Error al preparar la firma', {
        description: `No se logró preparar los datos para ser firmados.`,
      })
    }

    const signature = await localSignerHttpClient.POST('/sign', {
      body: dataToBeSigned.data,
    })

    if (signature.error) {
      setIsSigning(false)
      console.error(`Something went wrong when signing the data`, signature)

      return toast.error('Error al firmar', {
        description: `Hubo un error al firmar los datos de la firma con el token`,
      })
    }

    const signedPDF = await autosignApi.POST(
      '/v1/signing/{transactionId}/signature/{fieldId}/session/{sessionId}/cms/sign',
      {
        body: {
          ...signature.data,
          lat: geolocation?.latitude,
          lng: geolocation?.longitude,
        },
        params: {
          header: {
            authorization: signing.token,
          },
          path: {
            transactionId: signing.id,
            fieldId: currentFocusFieldId!,
            sessionId: signatureSession.id,
          },
        },
      }
    )

    if (signedPDF.error) {
      setIsSigning(false)
      console.error(`Something went wrong when signing the data`, signedPDF)

      return toast.error('Error al firmar', {
        description: `Hubo un error al firmar el documento con el token`,
      })
    }

    setSigned(true)

    await queryClient.invalidateQueries({
      queryKey: [buildSignatureSessionQueryKey(signatureSession.id)],
    })

    setIsSigning(false)
  }, [signatureSession, geolocation?.latitude, geolocation?.longitude])

  return {
    handleSign,
    isSigning,
    signed,
  }
}

type SignTrigger = 'manual' | 'auto'

const PendingSignatureWatcher = ({
  signatureSession,
}: {
  signatureSession: RetrieveSignatureSessionResponseBody
}) => {
  const tokenSignature = useTokenSignature(signatureSession)

  useReactiveSignatureSession(signatureSession, {
    onResponse: async (reactiveSession) => {
      if (reactiveSession.status === 'opened') {
        await tokenSignature.handleSign()
      }
    },
    reactiveQueryEnabled: true,
  })

  return <CTAButton loading />
}

const AutomaticallySignButton = ({
  signatureSession,
}: {
  signatureSession: RetrieveSignatureSessionResponseBody
}) => {
  const currentFocusFieldId = usePDFStore((state) => state.currentFocusFieldId)
  const signing = useSigning()

  const [signerOpened, setSignerOpened] = useState(false)

  const handleOpenSigner = useCallback(() => {
    const deepLink = `autosigntokenmanager://open?${qs.stringify({
      transactionId: signing.id,
      token: signing.token,
      signatureSessionId: signatureSession.id,
      fieldId: currentFocusFieldId,
    })}`

    window.open(deepLink)

    setSignerOpened(true)
  }, [signatureSession.id])

  if (signerOpened)
    return <PendingSignatureWatcher signatureSession={signatureSession} />

  return <CTAButton loading={signerOpened} onClick={handleOpenSigner} />
}

const ManualSignButton = ({
  signatureSession,
}: {
  signatureSession: RetrieveSignatureSessionResponseBody
}) => {
  const tokenSignature = useTokenSignature(signatureSession)

  useReactiveSignatureSession(signatureSession, {
    reactiveQueryEnabled: tokenSignature.signed || tokenSignature.isSigning,
  })

  return (
    <CTAButton
      loading={tokenSignature.isSigning}
      onClick={tokenSignature.handleSign}
    />
  )
}

export const TokenSignature = (props: SignatureSessionMethodProps) => {
  const [isLoading, setIsLoading] = useState(true)
  const [signTrigger, setSignTrigger] = useState<SignTrigger>()

  const queryClient = useQueryClient()

  const tryPair = useCallback(
    (signatureSession: RetrieveSignatureSessionResponseBody) => {
      const abortController = new AbortController()

      localSignerHttpClient
        .POST('/pair', {
          signal: abortController.signal,
          body: {
            fieldId: currentFocusFieldId!,
            signatureSessionId: signatureSession.id,
            token: signing.token,
            transactionId: signing.id,
          },
        })
        .then(async (pairResponse) => {
          if (pairResponse.error) {
            console.error(
              `Something went wrong when pairing the signature session`,
              pairResponse
            )

            setSignTrigger('auto')

            return
          }

          console.log(
            `Signature session paired successfully`,
            pairResponse.data
          )

          setSignTrigger('manual')

          await queryClient.invalidateQueries({
            queryKey: [buildSignatureSessionQueryKey(signatureSession.id)],
          })
        })
        .catch((error) => {
          setSignTrigger('auto')

          console.error(
            'Something went wrong when pairing the signature session',
            error
          )
        })
        .finally(() => setIsLoading(false))

      setTimeout(() => {
        abortController.abort()
      }, 2000)
    },
    []
  )

  const signatureSession = useSignatureSession({
    createSession: props.createSession,
    onCreated: tryPair,
  })

  const signing = useSigning()
  const currentFocusFieldId = usePDFStore((state) => state.currentFocusFieldId)

  console.log('signTrigger', signTrigger)

  return (
    <div className='w-full flex flex-col gap-4 items-center justify-center'>
      <div className='w-full text-left'>
        <p className='text-sm font-medium text-start'>
          Firma el documento con tu token USB
        </p>
      </div>
      <div>
        <div className='flex items-center gap-1 flex-row'>
          <p className='text-sm text-muted-foreground'>
            Inserta tu Token USB en la computadora para empezar 🔌{' '}
          </p>
        </div>

        <p className='text-sm text-muted-foreground italic'>
          Recuerda que debes tener instalado Autosign Token Manager previamente.{' '}
          <a
            onClick={() => {
              window.open(
                'https://help.autosign.io/es/articles/12476002-instalacion',
                '_blank'
              )
            }}
            href='https://help.autosign.io/es/articles/12476002-instalacion'
            className='hover:underline'
          >
            Guía de instalación
          </a>
        </p>
      </div>

      {isLoading || !signTrigger || !signatureSession.session ? (
        <CTAButton loading />
      ) : signTrigger === 'auto' ? (
        <AutomaticallySignButton signatureSession={signatureSession.session} />
      ) : (
        <ManualSignButton signatureSession={signatureSession.session} />
      )}

      <div className='space-x-1 flex flex-row items-center text-center'>
        <p className='text-xs text-muted-foreground'>¿Necesitas ayuda?</p>
        <div className='flex justify-center gap-2 text-xs'>
          <a
            onClick={() => {
              window.open(
                'https://help.autosign.io/es/articles/12478084-firma-de-documentos',
                '_blank'
              )
            }}
            className='text-primary hover:underline flex items-center gap-1 cursor-pointer'
            target='_blank'
            href='https://help.autosign.io/es/articles/12478084-firma-de-documentos'
          >
            Guía de firma
            <IconExternalLink className='h-3 w-3' />
          </a>
        </div>
      </div>
    </div>
  )
}
